import React from 'react';
import { Search, User, Menu } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import Logo from '@/components/UI/Logo';
import MegaMenu from '@/components/UI/MegaMenu';
import { Sheet, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import SideMenu from './SideMenu';
import SubscribeDialog from '@/components/UI/SubscribeDialog';
import SearchInput from '@/components/Search/SearchInput';
import SearchResultsList from '@/components/Search/SearchResultsList';
import { useSearchLogic } from '@/hooks/useSearchLogic';
import { CartDrawer } from '@/components/Shop/CartDrawer';

const Header = () => {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { searchResults, isSearching, handleSearchChange, query, clearSearch } = useSearchLogic();
  const [isSubscribeDialogOpen, setIsSubscribeDialogOpen] = React.useState(false);
  const handleSubscribeOpen = () => setIsSubscribeDialogOpen(true);
  const handleSubscribeClose = () => setIsSubscribeDialogOpen(false);

  return (
    <header className={cn(
      'fixed top-0 left-0 right-0 z-50 flex items-center justify-center transition-all duration-300 h-16 md:h-[72px]',
      resolvedTheme === 'dark' ? 'bg-netflix-black' : 'bg-background'
    )}>
      <div className="container mx-auto flex items-center justify-between px-4">
        <div className="flex items-center">
          <Logo className="mr-0" />
        </div>
        
        <div className="hidden md:flex flex-grow justify-center">
          <MegaMenu />
        </div>
        
        <div className="flex items-center space-x-4">
          <Popover>
            <PopoverTrigger asChild>
              <button className="text-foreground/80 hover:text-foreground transition-colors">
                <Search size={22} />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-96 md:w-screen md:max-w-4xl lg:max-w-6xl xl:max-w-7xl p-0 bg-background" align="end">
              <div className="p-4 space-y-4">
                <SearchInput
                  query={query}
                  onChange={handleSearchChange}
                  onClear={clearSearch}
                  placeholder="Search stories, videos, tags..."
                />
                
                {query && (
                  <div className="max-h-80 overflow-y-auto">
                    {isSearching ? (
                      <div className="text-center text-muted-foreground py-8">
                        <p>Searching...</p>
                      </div>
                    ) : searchResults.stories.length === 0 && searchResults.videos.length === 0 ? (
                      <div className="text-center text-muted-foreground py-8">
                        <p>No results found for "{query}"</p>
                      </div>
                    ) : (
                      <SearchResultsList
                        storyResults={searchResults.stories}
                        contentResults={searchResults.videos}
                        onItemClick={() => {}}
                        onTagClick={(tag) => {
                          handleSearchChange({ target: { value: tag } } as React.ChangeEvent<HTMLInputElement>);
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
          
          <button 
            className="text-foreground/80 hover:text-foreground transition-colors" 
            onClick={() => navigate('/profile')}
          >
            <User size={22} />
          </button>
          
          <button 
            className="text-foreground/80 hover:text-foreground transition-colors" 
            onClick={() => navigate('/wishlist')}
            title="Wishlist"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
            </svg>
          </button>
          
          <CartDrawer />
          
          <Sheet>
            <SheetTrigger asChild>
              <button className="text-foreground/80 hover:text-foreground transition-colors">
                <Menu size={22} />
              </button>
            </SheetTrigger>
            <SideMenu onSubscribeOpen={handleSubscribeOpen} />
          </Sheet>
          <SubscribeDialog isOpen={isSubscribeDialogOpen} onClose={handleSubscribeClose} />
        </div>
      </div>
    </header>
  );
};

export default Header;